import React, { useEffect, useState } from 'react';

import { EveryActionCustomElementMap } from './EveryActionCustomElementMap.component';
import { EveryActionCustomSubmitButton } from './EveryActionCustomElement';
import * as Styled from './CustomEveryActionForm.styles';

import * as FormStyled from '+/styles/styledComponents/Form.styles';
import { FormHeader } from '+/components/FormHeader';
import { Markdown } from '+/components/Markdown';
import { ColorTheme } from '+/components/ColorTheme';
import { replaceStringWithLocationState } from '+/utils/replaceStringWithLocationState';
import { t } from '+/utils/textDictionary';
import { useTypedLocation } from '+/utils/hooks/useGatsbyLocation';
import {
  DOUBLE_THE_DONATION_CAMPAIGN,
  DoubleTheDonationState,
} from '+/contexts/DoubleTheDonationContext';

interface CustomEveryActionFormProps {
  formElements: any;
  customEveryActionFormData: CustomEveryActionFormComponent;
  suppliedOptions: any;
  handleSubmit: () => void;
  isSubmitted: boolean;
  isValid: boolean;
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
}

const getFirstNumberSequence = str => {
  const match = str.match(/\d+/);
  return match ? match[0] : null;
};

export const CustomEveryActionForm = ({
  formElements,
  customEveryActionFormData,
  suppliedOptions,
  isSubmitted,
  ...formikMethods
}: CustomEveryActionFormProps) => {
  const gatsbyLocationState = useTypedLocation<{
    donationAmount: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    doubleTheDonationState?: DoubleTheDonationState;
  }>();
  const { border, customCss, handlePreSubmit, headerTitle, headerCopy, submitText, colorTheme } =
    customEveryActionFormData;

  const hasDoubleTheDonation =
    !!gatsbyLocationState?.doubleTheDonationState?.doubleTheDonationCompanyId &&
    window.doublethedonation;

  const formattedHeaderTitle = replaceStringWithLocationState(headerTitle);
  const formattedHeaderCopy = replaceStringWithLocationState(headerCopy);

  useEffect(() => {
    const email = gatsbyLocationState?.emailAddress || '';

    if (gatsbyLocationState?.doubleTheDonationState) {
      const { donationIdentifier, doubleTheDonationCompanyId } =
        gatsbyLocationState.doubleTheDonationState;

      if (window.doublethedonation) {
        window.doublethedonation.plugin.load_config();
        window.doublethedonation.plugin.load_plugin();

        window.doublethedonation.plugin.set_company(doubleTheDonationCompanyId);
        window.doublethedonation.plugin.set_donation_campaign(DOUBLE_THE_DONATION_CAMPAIGN);
        window.doublethedonation.plugin.set_donation_identifier(
          gatsbyLocationState?.doubleTheDonationState?.donationIdentifier || '',
        );
      }

      // If Double the Donation is active and if we have the required data, register the donation
      if (window.doublethedonation && doubleTheDonationCompanyId && donationIdentifier) {
        const doubleTheDonationPayload = {
          '360matchpro_public_key': process.env.GATSBY_DOUBLE_THE_DONATION_API_KEY,
          campaign: DOUBLE_THE_DONATION_CAMPAIGN,
          donation_identifier: donationIdentifier,
          donation_amount: getFirstNumberSequence(gatsbyLocationState?.donationAmount),
          donor_first_name: gatsbyLocationState?.firstName,
          donor_last_name: gatsbyLocationState?.lastName,
          donor_email: email,
          doublethedonation_company_id: doubleTheDonationCompanyId,
        };

        window.doublethedonation.integrations.core.register_donation(doubleTheDonationPayload);
      }
    }
  }, [gatsbyLocationState, window.doublethedonation]);

  return (
    <ColorTheme colorTheme={colorTheme}>
      <FormStyled.Container customCss={customCss}>
        <Styled.GlobalFastActionSignUpModalStyles />
        <FormHeader
          className="cstm-form__header"
          headerTitle={<h2>{formattedHeaderTitle}</h2>}
          headerCopy={<Markdown>{formattedHeaderCopy}</Markdown>}
          padded
        />
        {hasDoubleTheDonation ? (
          <>
            <FormStyled.Container customCss={customCss}>
              <FormHeader
                className="cstm-form__header"
                headerTitle={<h2>Employer Match</h2>}
                headerCopy={
                  <Markdown>
                    **Complete your employer match to make even more of an impact — for the
                    animals.**
                  </Markdown>
                }
                padded
              />
            </FormStyled.Container>
            <Styled.Wrapper padded className="cstm-double-the-donation">
              <div id="dd-container" />
            </Styled.Wrapper>
          </>
        ) : (
          <FormStyled.Body className="cstm-form__body">
            <FormStyled.Section className="cstm-form__section" border={border}>
              <div className="cstm-form__form">
                {isSubmitted ? (
                  <Styled.FormThanks className="cstm-thanks-message">
                    {t('CustomForm.ThankYou')}
                  </Styled.FormThanks>
                ) : (
                  <>
                    {formElements.map(e =>
                      EveryActionCustomElementMap(e, formikMethods, suppliedOptions),
                    )}
                    <EveryActionCustomSubmitButton
                      buttonText={submitText || 'Submit'}
                      handleSubmit={formikMethods.handleSubmit}
                      handlePreSubmit={handlePreSubmit}
                      isDisabled={!formikMethods.isValid}
                    />
                  </>
                )}
              </div>
            </FormStyled.Section>
          </FormStyled.Body>
        )}
      </FormStyled.Container>
    </ColorTheme>
  );
};
